import React, { useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import { useTabActivity } from '../../contexts/TabActivityContext';
import { Send } from 'lucide-react';
import { ChatInputProps } from './types';

export const ChatInput: React.FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  inputDisabled = false,
  sendDisabled = false,
  placeholder = 'Ask about your video content...',
  sendDisabledTitle = 'Select at least one index or extraction run to send',
}) => {
  const { isActive } = useTabActivity();
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(textarea.scrollHeight, 150);
      textarea.style.height = `${newHeight}px`;
    }
  }, []);

  useEffect(() => {
    if (isActive) adjustHeight();
  }, [isActive, value, adjustHeight]);

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && !sendDisabled) {
        onSend();
      }
    }
  };

  const handleSendClick = () => {
    if (value.trim() && !sendDisabled) {
      onSend();
    }
  };

  const canSend = Boolean(value.trim()) && !sendDisabled;

  return (
    <div className="agentic-chat-input">
      <div className="input-container">
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={inputDisabled}
          rows={1}
          className="chat-textarea"
        />
        <button
          className={`send-button ${canSend ? 'active' : ''}`}
          onClick={handleSendClick}
          disabled={!canSend}
          title={sendDisabled ? sendDisabledTitle : 'Send message'}
        >
          <Send size={18} />
        </button>
      </div>
      <div className="input-hint">
        Press Enter to send, Shift+Enter for new line
      </div>
    </div>
  );
};

export default ChatInput;
